package com.gloopgo.game.viewmodel

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.gloopgo.game.network.MultiplayerRepository
import kotlinx.coroutines.Job
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.launch

data class AuthUiState(
    val isCheckingSession: Boolean = true,
    val isLoading: Boolean = false,
    val isAuthenticated: Boolean = false,
    val userId: String = "",
    val userName: String = "",
    val errorMessage: String? = null
)

class AuthViewModel(
    private val repository: MultiplayerRepository
) : ViewModel() {

    private val _uiState = MutableStateFlow(AuthUiState())
    val uiState: StateFlow<AuthUiState> = _uiState.asStateFlow()

    private var checkSessionJob: Job? = null

    init {
        checkSession()
    }

    fun checkSession() {
        checkSessionJob?.cancel()
        checkSessionJob = viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isCheckingSession = true, errorMessage = null)
            try {
                val currentUid = repository.getCurrentUserId()
                if (!currentUid.isNullOrEmpty()) {
                    _uiState.value = _uiState.value.copy(
                        isCheckingSession = false,
                        isAuthenticated = true,
                        userId = currentUid,
                        errorMessage = null
                    )
                } else {
                    val defaultUserName = ""
                    val uid = repository.signInAnonymously(defaultUserName)
                    _uiState.value = _uiState.value.copy(
                        isCheckingSession = false,
                        isAuthenticated = true,
                        userId = uid,
                        userName = defaultUserName,
                        errorMessage = null
                    )
                }
            } catch (e: Throwable) {
                _uiState.value = _uiState.value.copy(
                    isCheckingSession = false,
                    isAuthenticated = false,
                    errorMessage = e.message ?: "Failed to sign in anonymously. Please try again."
                )
            }
        }
    }


    /**
     * Drops cached auth UI state after clear-user-data so Splash re-runs anonymous sign-in.
     */
    fun clearLocalSession() {
        checkSessionJob?.cancel()
        checkSessionJob = null
        _uiState.value = AuthUiState(isCheckingSession = true, isAuthenticated = false, userId = "")
    }

    fun signInAsGuest(displayName: String = "Guest") {
        viewModelScope.launch {
            _uiState.value = _uiState.value.copy(isLoading = true, errorMessage = null)
            try {
                val uid = repository.signInAnonymously(displayName)
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    isAuthenticated = true,
                    userId = uid,
                    userName = displayName
                )
            } catch (e: Throwable) {
                _uiState.value = _uiState.value.copy(
                    isLoading = false,
                    errorMessage = e.message ?: "Failed to sign in as guest"
                )
            }
        }
    }
}
